import { inject, Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { PUBLIC_MEDIA_BUCKET, supabase } from '../../core/supabase/supabase.client';
import { ImageAssetUploadService } from '../utils/image-asset-upload.service';

/* -------------------- DTOs -------------------- */
interface CategoryRow {
  id: number;
  name: string;
  slug: string;
  parent_id: number | null;
  level: number;
  position: number;
  is_active: boolean;
  icon?: { bucket: string; path: string } | null;
}
export interface CategoryDto {
  id: number;
  name: string;
  slug: string;
  parentId?: number | null;
  level: number;
  position: number;
  iconUrl: string;
  isActive: boolean;
  showInCategoryScreen?: boolean;
  children?: CategoryDto[];
}

export interface CategoryTreeResponse {
  data: CategoryDto[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CategoryProductCount {
  category_id: number;
  direct_product_count: number;
  product_count: number;
}

export interface CreateCategoryPayload {
  name: string;
  parentId?: number | null;
  level?: number | null;
  position?: number | null;
  expectedParentId?: number | null;
}

export interface CategoryPositionUpdate {
  id: number;
  position: number;
}

@Injectable({ providedIn: 'root' })
export class CategoriesService {
  private table = 'categories';
  private readonly imageAssets = inject(ImageAssetUploadService);
  private readonly bucket = PUBLIC_MEDIA_BUCKET;

  getProductCounts(categoryIds: number[]): Observable<CategoryProductCount[]> {
    return from(supabase.rpc('admin_category_product_counts', { p_category_ids: categoryIds })
      .then(({ data, error }) => {
        if (error) throw error;
        return (data ?? []).map((row: CategoryProductCount) => ({
          category_id: Number(row.category_id),
          direct_product_count: Number(row.direct_product_count),
          product_count: Number(row.product_count),
        }));
      }));
  }

  /* ---------------- CREATE ---------------- */
  createCategory(payload: CreateCategoryPayload): Observable<CategoryDto> {
    return from(
      supabase
        .from(this.table)
        .insert({
          name: payload.name,
          parent_id: payload.parentId ?? null,
          level: payload.level ?? 0,
          position: payload.position ?? 0,
        })
        .select(
          `
  *,
  files:files!categories_icon_file_fk (
    bucket,
    path,
    purpose,
    is_primary
  )
`
        )

        .single()
        .then(({ data, error }) => {
          if (error) throw error;
          return this.mapCategory(data);
        })
    );
  }

  /* ---------------- TREE (PAGINATED) ---------------- */
  getCategoryTree(
    page = 1,
    limit = 10,
    search = '',
    sortBy = 'position',
    order: 'ASC' | 'DESC' = 'ASC',
    isActive?: boolean
  ): Observable<CategoryTreeResponse> {
    return from(
      (async () => {
        const safePage = Math.max(1, Number(page) || 1);
        const safeLimit = Math.max(1, Number(limit) || 10);
        const fromIndex = (safePage - 1) * safeLimit;
        const toIndex = fromIndex + safeLimit - 1;

        let rootQuery = supabase
          .from(this.table)
          .select(
            `
            *,
            icon:files!categories_icon_file_fk (
              bucket,
              path
            )
            `,
            { count: 'exact' }
          )
          .is('parent_id', null)
          .order(sortBy, { ascending: order === 'ASC' })
          .order('name', { ascending: true })
          .order('id', { ascending: true })
          .range(fromIndex, toIndex);

        if (search) rootQuery = rootQuery.ilike('name', `%${search}%`);
        if (typeof isActive !== 'undefined') {
          rootQuery = rootQuery.eq('is_active', isActive);
        }

        const { data, error, count } = await rootQuery;
        if (error) throw error;

        const rootRows = data ?? [];
        const childRows = await this.getDescendantRows(
          rootRows.map((row) => row.id),
          isActive
        );
        const total = count ?? 0;
        const paginatedTree = this.buildTree([...rootRows, ...childRows]);
        const direction = order === 'ASC' ? 1 : -1;
        paginatedTree.sort((a, b) => {
          const primary =
            sortBy === 'position'
              ? a.position - b.position
              : a.name.localeCompare(b.name);

          return (
            primary * direction ||
            a.name.localeCompare(b.name) ||
            a.id - b.id
          );
        });

        // ✅ paginate ONLY root categories
        return {
          data: paginatedTree,
          meta: {
            page: safePage,
            limit: safeLimit,
            total,
            totalPages: Math.max(1, Math.ceil(total / safeLimit)),
          },
        };
      })()
    );
  }

  /* ---------------- TREE (ALL) ---------------- */
  getCategoryTreeAll(activeOnly = true): Observable<CategoryDto[]> {
    return from(
      (async () => {
        const rows: CategoryRow[] = [];
        for (let offset = 0; ; offset += 500) {
          let query = supabase.from(this.table).select('*, icon:files!categories_icon_file_fk(bucket,path)');
          if (activeOnly) query = query.eq('is_active', true);
          const { data, error } = await query.order('id', { ascending: true }).range(offset, offset + 499);
          if (error) throw error;
          rows.push(...(data ?? []));
          if ((data ?? []).length < 500) break;
        }
        return this.buildTree(rows);
      })()
    );
  }
  getRootCategories(): Observable<CategoryDto[]> {
    return from(
      supabase
        .from(this.table)
        .select(
          `
        *,
        icon:files!categories_icon_file_fk (
          bucket,
          path
        )
        `
        )
        .eq('level', 0)
        .eq('is_active', true)
        .order('position', { ascending: true })
        .order('name', { ascending: true })
        .then(({ data, error }) => {
          if (error) throw error;
          return (data ?? []).map((row: any) => this.mapCategory(row));
        })
    );
  }

  async getCategoryLevel(categoryId: number): Promise<number> {
    const { data, error } = await supabase
      .from(this.table)
      .select('level')
      .eq('id', categoryId)
      .single();

    if (error || !data) throw error;

    return data.level;
  }
  async getFullCategoryChain(categoryId: number): Promise<CategoryDto[]> {
    const chain: CategoryDto[] = [];
    let currentId: number | null = categoryId;

    while (currentId) {
      const { data, error }: { data: any; error: any } = await supabase
        .from(this.table)
        .select(
          `
          *,
          icon:files!categories_icon_file_fk (
            bucket,
            path
          )
          `
        )
        .eq('id', currentId)
        .single();

      if (error || !data) break;

      // add to START so order is level 0 → leaf
      chain.unshift(this.mapCategory(data));

      currentId = data.parent_id;
    }

    return chain;
  }
  getChildCategoriesByParentId(parentId: number): Observable<CategoryDto[]> {
    return from(
      supabase
        .from(this.table)
        .select(
          `
        *,
        icon:files!categories_icon_file_fk (
          bucket,
          path
        )
        `
        )
        .eq('parent_id', parentId)
        .eq('is_active', true)
        .order('position', { ascending: true })
        .order('name', { ascending: true })
        .then(({ data, error }) => {
          if (error) throw error;
          return data ?? [];
        })
    );
  }

  private inheritIconFromChildren(categories: CategoryDto[]): void {
    categories.forEach((category) => {
      // recurse first
      if (category.children?.length) {
        this.inheritIconFromChildren(category.children);

        // if parent has no icon, inherit from first child with icon
        if (!category.iconUrl) {
          const childWithIcon = category.children.find((c) => c.iconUrl);
          if (childWithIcon) {
            category.iconUrl = childWithIcon.iconUrl;
          }
        }
      }
    });
  }

  /* ---------------- UPDATE ---------------- */
  setCategoryScreenVisibility(id: number, visible: boolean): Observable<CategoryDto> {
    return from(supabase.rpc('admin_set_category_screen_visibility', {
      p_category_id: id, p_visible: visible,
    }).then(({ data, error }) => {
      if (error) throw error;
      return this.mapCategory(data);
    }));
  }

  updateCategory(
    id: number,
    payload: CreateCategoryPayload
  ): Observable<CategoryDto> {
    if ('parentId' in payload) {
      return from(supabase.rpc('admin_move_category', {
        p_category_id: id, p_parent_id: payload.parentId ?? null,
        p_expected_parent_id: payload.expectedParentId ?? null,
        p_name: payload.name, p_position: payload.position ?? 0,
      }).then(({ data, error }) => { if (error) throw error; return this.mapCategory(data); }));
    }
    const update: { name: string; position?: number } = { name: payload.name };
    if (payload.position != null) update.position = payload.position;

    return from(
      supabase
        .from(this.table)
        .update(update)
        .eq('id', id)
        .select(
          `
  *,
  files:files!categories_icon_file_fk (
    bucket,
    path,
    purpose,
    is_primary
  )
`
        )

        .single()
        .then(({ data, error }) => {
          if (error) throw error;
          return this.mapCategory(data);
        })
    );
  }

  updateCategoryPositions(
    updates: CategoryPositionUpdate[]
  ): Observable<void> {
    return from(
      (async () => {
        if (!updates.length) return;

        await Promise.all(
          updates.map(async ({ id, position }) => {
            const { error } = await supabase
              .from(this.table)
              .update({ position })
              .eq('id', id);

            if (error) throw error;
          })
        );
      })()
    );
  }

  /* ---------------- TOGGLE ACTIVE ---------------- */
  toggleCategory(id: number): Observable<CategoryDto> {
    return from(
      supabase
        .rpc('toggle_category_status', { category_id: id })
        .then(({ data, error }) => {
          if (error) throw error;
          return this.mapCategory(data[0]); // ✅ array → first row
        })
    );
  }

  /* ---------------- UPLOAD ICON ---------------- */
  uploadCategoryIcon(categoryId: number, file: File): Observable<string> {
    return from(
      (async () => {
        /* 0️⃣ Compress image */
        const asset = await this.imageAssets.upload({
          file,
          bucket: this.bucket,
          basePath: `categories/${categoryId}`,
          kind: 'category',
          entityType: 'category',
          entityId: categoryId,
          purpose: 'icon',
          isPrimary: true,
        });

        /* 1️⃣ Upload to storage */


        /* 2️⃣ Insert into files table AND get file id */


        /* 3️⃣ 🔥 LINK FILE TO CATEGORY (THIS WAS MISSING) */
        const { error: categoryError } = await supabase
          .from('categories')
          .update({ icon_file_id: asset.fileId })
          .eq('id', categoryId);

        if (categoryError) throw categoryError;

        /* 4️⃣ Return public URL */
        return asset.masterUrl;
      })()
    );
  }

  /* ---------------- HELPERS ---------------- */
  private getIconUrl(
    file?: { bucket: string; path: string } | null
  ): string | null {
    if (!file?.bucket || !file?.path) return null;

    const { data } = supabase.storage.from(file.bucket).getPublicUrl(file.path);

    return data.publicUrl ?? null;
  }

  private async getDescendantRows(
    parentIds: number[],
    isActive?: boolean
  ): Promise<any[]> {
    if (!parentIds.length) return [];

    let childrenQuery = supabase
      .from(this.table)
      .select(
        `
        *,
        icon:files!categories_icon_file_fk (
          bucket,
          path
        )
        `
      )
      .in('parent_id', parentIds)
      .order('position', { ascending: true })
      .order('name', { ascending: true });

    if (typeof isActive !== 'undefined') {
      childrenQuery = childrenQuery.eq('is_active', isActive);
    }

    const { data, error } = await childrenQuery;
    if (error) throw error;

    const rows = data ?? [];
    const nextParentIds = rows.map((row) => row.id);

    return [
      ...rows,
      ...(await this.getDescendantRows(nextParentIds, isActive)),
    ];
  }

  private mapCategory(row: any): CategoryDto {
    // normalize icon (object OR array)
    const iconRow = Array.isArray(row.icon) ? row.icon[0] : row.icon;

    const iconUrl =
      iconRow?.bucket && iconRow?.path
        ? supabase.storage.from(iconRow.bucket).getPublicUrl(iconRow.path).data
            .publicUrl
        : '';

    return {
      id: row.id,
      name: row.name,
      slug: row.slug,
      parentId: row.parent_id,
      level: row.level,
      position: row.position ?? 0,
      iconUrl,
      isActive: row.is_active,
      showInCategoryScreen: row.show_in_category_screen !== false,
    };
  }

  private buildTree(rows: any[]): CategoryDto[] {
    const map = new Map<number, CategoryDto>();
    const roots: CategoryDto[] = [];

    // 1️⃣ Map all categories
    rows.forEach((row) => {
      // 🔥 FIX: normalize icon (array OR object)
      const iconRow = Array.isArray(row.icon) ? row.icon[0] : row.icon;

      const iconUrl =
        iconRow?.bucket && iconRow?.path
          ? supabase.storage.from(iconRow.bucket).getPublicUrl(iconRow.path)
              .data.publicUrl
          : '';

      map.set(row.id, {
        id: row.id,
        name: row.name,
        slug: row.slug,
        parentId: row.parent_id,
        level: row.level,
        position: row.position ?? 0,
        isActive: row.is_active,
        showInCategoryScreen: row.show_in_category_screen !== false,
        iconUrl,
        children: [],
      });
    });

    // 2️⃣ Build hierarchy
    map.forEach((category) => {
      if (category.parentId) {
        const parent = map.get(category.parentId);
        parent?.children?.push(category);
      } else {
        roots.push(category);
      }
    });

    const sortByPosition = (categories: CategoryDto[]) => {
      categories.sort(
        (a, b) =>
          a.position - b.position ||
          a.name.localeCompare(b.name) ||
          a.id - b.id
      );
      categories.forEach((category) => {
        if (category.children?.length) sortByPosition(category.children);
      });
    };

    sortByPosition(roots);
    return roots;
  }
}
